/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ----------------------------------------------------------------------------
 *   CostControlsArea (Spec 256 — Backup Cost Governance, US1 + US2 + US3)
 *   The "Cost & Controls" tab body:
 *     - fleet status roll-up (StatCards),
 *     - GLOBAL master switches for WORM / verification / secondary+vault copies,
 *       each with its directional (non-currency) cost note,
 *     - a per-plan table with EFFECTIVE state + source and inline per-plan
 *       toggles (override / opt-out), and the worst-case egress route.
 *   WORM is Admin-only; verification + secondary are Operator+; every flip is
 *   audited server-side. Switches default to preserving current behavior.
 * ============================================================================
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ClipboardCheck, Copy, HardDrive, Globe, HelpCircle, RotateCcw } from 'lucide-react';
import {
  Card,
  StatCard,
  DataTable,
  Badge,
  Toggle,
  Button,
  Modal,
  DashboardPanel,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { costApi, type ControlDomain, type ControlState, type PlanStatusRow } from '../../api/cost';
import { CONTROL_COST_NOTES, routeNote } from './costNotes';
import { AdvisorPanel } from './AdvisorPanel';
import { OffendersPanel } from './OffendersPanel';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default';

function routeVariant(routeClass: string): BadgeVariant {
  if (routeClass === 'unknown') return 'default';
  if (routeClass.includes('nat-internet') && routeClass.includes('cross-region')) return 'danger';
  if (routeClass.includes('cross-region') || routeClass.includes('nat-internet')) return 'warning';
  return 'success';
}

const SourceTag: React.FC<{ source: ControlState['source'] }> = ({ source }) => (
  <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-tertiary)]">{source}</span>
);

export const CostControlsArea: React.FC = () => {
  const qc = useQueryClient();
  const { isAdmin, isOperatorOrAbove } = useAuth();
  const canWorm = isAdmin();
  const canManage = isOperatorOrAbove();
  const [wormOffConfirm, setWormOffConfirm] = React.useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['backup-cost-status'],
    queryFn: () => costApi.getStatus(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['backup-cost-status'] });

  const globalMut = useMutation({
    mutationFn: (v: { domain: ControlDomain; value: boolean }) => costApi.setGlobalControl(v.domain, v.value),
    onSuccess: invalidate,
  });
  const planMut = useMutation({
    mutationFn: (v: { planId: string; domain: ControlDomain; value: boolean | null }) =>
      costApi.setPlanControl(v.planId, v.domain, v.value),
    onSuccess: invalidate,
  });
  const busy = globalMut.isPending || planMut.isPending;

  if (isLoading) return <LoadingState rows={6} />;
  if (isError || !data) {
    return <ErrorState title="Could not load cost status" message="Please try again." onRetry={() => refetch()} />;
  }

  const { fleet, globals, plans } = data;

  // ── per-plan control cell: effective toggle + source ──
  // 🛑 Spec 270: the inherit-reset arm is gone with the 'secondary' domain, which was the only
  // nullable one (null = inherit the global). worm/verification are both hard booleans.
  const controlCell = (
    row: PlanStatusRow,
    domain: ControlDomain,
    state: ControlState,
  ): React.ReactNode => {
    // WORM / verification are MASTER-gated: when the global master is off the plan value cannot
    // take effect, so the per-plan toggle is disabled (and the effective state reads off).
    const masterOff = (domain === 'worm' && !globals.worm) || (domain === 'verification' && !globals.verification);
    const roleOk = domain === 'worm' ? canWorm : canManage;
    const disabled = busy || !roleOk || masterOff;
    return (
      <span className="inline-flex items-center gap-2">
        <Toggle
          checked={state.effective}
          disabled={disabled}
          aria-label={`${domain} for ${row.name}`}
          onChange={(value) => planMut.mutate({ planId: row.planId, domain, value })}
        />
        <SourceTag source={state.source} />
        {/* Spec 270: inherit-reset removed with the 'secondary' domain */}
      </span>
    );
  };

  const columns: DataTableColumn<PlanStatusRow>[] = [
    {
      key: 'name',
      header: 'Plan',
      render: (r) => <span className="font-medium text-[var(--color-text-primary)]">{r.name}</span>,
      sortValue: (r) => r.name,
    },
    {
      key: 'worm',
      header: 'WORM',
      render: (r) => controlCell(r, 'worm', r.worm),
      sortValue: (r) => (r.worm.effective ? 1 : 0),
    },
    {
      key: 'verification',
      header: 'Verification',
      render: (r) => controlCell(r, 'verification', r.verification),
      sortValue: (r) => (r.verification.effective ? 1 : 0),
    },
    {
      key: 'storage',
      header: 'Storage target',
      render: (r) =>
        r.storageTarget ? (
          <span className="text-[var(--color-text-secondary)]">{r.storageTarget}</span>
        ) : (
          <span className="text-[var(--color-text-tertiary)]">—</span>
        ),
      sortValue: (r) => r.storageTarget ?? '',
    },
    {
      key: 'route',
      header: 'Route',
      render: (r) => (
        <span title={routeNote(r.pathAxis).text}>
          <Badge variant={routeVariant(r.routeClass)}>{r.routeClass}</Badge>
        </span>
      ),
      sortValue: (r) => r.routeClass,
    },
  ];

  const globalSwitch = (
    domain: ControlDomain,
    noteKey: keyof typeof CONTROL_COST_NOTES,
    value: boolean,
    roleOk: boolean,
    Icon: React.ComponentType<{ className?: string }>,
    label: string,
    onToggle: (v: boolean) => void,
  ) => (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-medium text-[var(--color-text-primary)]">
          <Icon className="h-4 w-4 text-[var(--color-accent)]" />
          {label}
        </span>
        <Toggle
          checked={value}
          disabled={busy || !roleOk}
          aria-label={`Global ${domain}`}
          onChange={onToggle}
        />
      </div>
      <p className="text-xs text-[var(--color-text-secondary)]">{CONTROL_COST_NOTES[noteKey].text}</p>
      {!roleOk && (
        <p className="text-[10px] uppercase tracking-wide text-[var(--color-text-tertiary)]">
          {domain === 'worm' ? 'Admin only' : 'Operator or Admin only'}
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="WORM on"
          value={`${fleet.worm.on} / ${fleet.worm.total}`}
          icon={<Shield className="h-5 w-5" />}
          subtext="Immutable versions grow stored data"
        />
        <StatCard
          label="Verification on"
          value={`${fleet.verification.on} / ${fleet.verification.total}`}
          icon={<ClipboardCheck className="h-5 w-5" />}
          subtext="Restore-tests add compute"
        />
        <StatCard label="Storage targets" value={fleet.storageTargets} icon={<HardDrive className="h-5 w-5" />} />
        <StatCard
          label="Cross-region plans"
          value={fleet.crossRegion}
          icon={<Globe className="h-5 w-5" />}
          valueClassName={fleet.crossRegion > 0 ? 'text-[var(--color-status-warning)]' : undefined}
          subtext="Cross-region transfer cost"
        />
        <StatCard
          label="Unknown route"
          value={fleet.unknownRoute}
          icon={<HelpCircle className="h-5 w-5" />}
          subtext="Not classified — not assumed cheap"
        />
      </div>

      <DashboardPanel
        title="Global switches"
        subtitle="Master defaults for the whole fleet. Each states where it drives cost — never a dollar amount. WORM and verification are hard masters (off = off everywhere); the secondary default applies to plans that don't set their own."
        titleInfo="Flipping a switch takes effect on the next backup/verification cycle — no deploy. Every change is recorded to the control audit."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {globalSwitch('worm', 'worm', globals.worm, canWorm, Shield, 'WORM / immutability', (v) => {
            // Turning WORM off is non-destructive — already-locked points stay immutable until their
            // unlock date. Warn (never block) when there are locked points so the operator isn't
            // surprised that storage doesn't free immediately.
            if (!v && fleet.lockedPoints > 0) setWormOffConfirm(true);
            else globalMut.mutate({ domain: 'worm', value: v });
          })}
          {globalSwitch('verification', 'verification', globals.verification, canManage, ClipboardCheck, 'Recoverability — verification', (v) =>
            globalMut.mutate({ domain: 'verification', value: v }),
          )}
          {/* 🛑 Spec 270 FR-019a: the 'Global secondary' master switch is REMOVED. Flipping it
              wrote global_settings.secondary_copy_backup_enabled and drove
              backup_plans.secondary_copy_override -- the column migration 280 drops, and the
              exact mechanism migration 252 mass-enabled on 246 plans to cause the 2026-06/07
              cost incident. Leaving a live switch for a deleted feature is worse than removing
              it: an operator flipping it would get either an error or, after 280, nothing. */}
        </div>
      </DashboardPanel>

      <DashboardPanel
        title="Per-plan controls & route"
        subtitle="Effective WORM / verification / secondary state (with its source), the storage target, and the egress route class. Toggle a plan to override the global; the ↺ reset returns a secondary override to the global default."
        titleInfo="Cross-region and NAT paths drive egress. When a master switch is off, its per-plan toggles are disabled because the plan value cannot take effect."
      >
        {plans.length === 0 ? (
          <EmptyState title="No backup plans" message="No active or paused plans to show yet." />
        ) : (
          <Card padding="none">
            <DataTable columns={columns} rows={plans} rowKey={(r) => r.planId} minWidth="64rem" />
          </Card>
        )}
      </DashboardPanel>

      <OffendersPanel />

      <AdvisorPanel />

      {/* 🛑 Spec 270 FR-019a: the Admin-only ReclaimPanel is REMOVED. It reclaimed orphaned
          SECONDARY copies via GET /cost/reclaim/preview + POST /cost/reclaim, both of which are
          gone. Left in place it would have rendered an error on every Admin visit to this tab --
          invisible to BOTH typechecks, because the call crosses the untyped HTTP seam. */}

      <Modal
        isOpen={wormOffConfirm}
        onClose={() => setWormOffConfirm(false)}
        title="Turn WORM off globally?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setWormOffConfirm(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={busy}
              onClick={() => {
                globalMut.mutate({ domain: 'worm', value: false });
                setWormOffConfirm(false);
              }}
            >
              Turn WORM off
            </Button>
          </div>
        }
      >
        <p className="text-sm text-[var(--color-text-secondary)]">
          This only stops NEW backups from being locked. The{' '}
          <span className="font-medium text-[var(--color-text-primary)]">{fleet.lockedPoints}</span> already-locked
          recovery point{fleet.lockedPoints === 1 ? '' : 's'} stay immutable until{' '}
          {fleet.lockedPoints === 1 ? 'its' : 'their'} lock expires — their storage is not freed by this change.
        </p>
      </Modal>
    </div>
  );
};

export default CostControlsArea;
